import { Typography, useTheme } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { CumulativeScoreQuestion } from 'sharedComponents/Questions/components/CumulativeScoreQuestion';
import { FinalRecommendationQuestion } from 'sharedComponents/Questions/components/FinalRecommendationQuestion';
import {
  MultiSelectMultipleChoice,
  SingleMultipleChoiceQuestion,
} from 'sharedComponents/Questions/components/MultipleChoiceQuestion';
import { QuestionControllerProps, SharedQuestionAndAnswerSectionProps } from './types';
import { RatingQuestion } from 'sharedComponents/Questions/components/RatingQuestion';
import { RubricQuestion } from 'sharedComponents/Questions/components/RubricQuestion';
import { AttachmentQuestion } from './AttachmentQuestion';
import { scorecardQType } from 'utils/enums';
import { TextQuestion } from './TextQuestion';
import { FinalRecommendation } from 'utils/typedEnums';

const QuestionController = <T,>({
  name,
  children,
  required,
  rules,
}: QuestionControllerProps<T>) => (
  <Controller
    name={name}
    control={useFormContext().control}
    rules={{
      required: required ? 'This field is required' : false,
      ...rules,
    }}
    render={({ field: { onChange, value }, fieldState: { error } }) =>
      children({ onChange, value, error })
    }
  />
);

export const ErrorText: React.FC<{ dataTestId: string }> = ({ dataTestId }) => {
  const theme = useTheme();

  return (
    <Typography
      data-testid={dataTestId}
      sx={{ paddingTop: theme.spacing(1), color: theme.palette.error.main }}
    >
      This field is required
    </Typography>
  );
};

export const SharedQuestionAndAnswerSection: React.FC<SharedQuestionAndAnswerSectionProps> = ({
  answers,
  formFieldPrefix = 'answers',
  index,
  isReadOnly,
  onCumulativeScoreUpdate,
  question,
  testIdPrefix,
}) => {
  const { watch } = useFormContext();
  const isScorecardComplete = watch('scorecard_complete');
  const isReferenceComplete = watch('is_complete');
  const isDraft = !isScorecardComplete && !isReferenceComplete;

  switch (question.question_type) {
    case scorecardQType.rating:
      return (
        <QuestionController<number>
          rules={{ min: 0, max: question.scale }}
          required={isDraft ? false : question.is_required}
          name={`${formFieldPrefix}.${index}.answer_rating`}
        >
          {({ onChange, value, error }) => (
            <>
              <RatingQuestion
                dataTestId={`${testIdPrefix}-rating-${question.id}`}
                isRequired={question.is_required}
                maxScore={question.scale}
                question={question.question_text}
                onChange={(e) => {
                  const numericValue = e.target.valueAsNumber;
                  onChange(isNaN(numericValue) ? null : numericValue);
                  onCumulativeScoreUpdate?.();
                }}
                value={value}
                defaultValue={answers?.rating}
                isReadOnly={isReadOnly}
              />
              {error && (
                <ErrorText dataTestId={`${testIdPrefix}-rating-${question.id}-error-text`} />
              )}
            </>
          )}
        </QuestionController>
      );
    case scorecardQType.nimble:
    case scorecardQType.text:
      return (
        <QuestionController
          required={isDraft ? false : question.is_required}
          name={`${formFieldPrefix}.${index}.answer_text`}
        >
          {({ onChange, error }) => (
            <>
              <TextQuestion
                dataTestId={`${testIdPrefix}-text-${question.id}`}
                defaultValue={answers?.text}
                multiline
                onChange={(e) => onChange(e.target.value)}
                question={question.question_text}
                rows={4}
                isRequired={question.is_required}
                isReadOnly={isReadOnly}
              />
              {error && <ErrorText dataTestId={`${testIdPrefix}-text-${question.id}-error-text`} />}
            </>
          )}
        </QuestionController>
      );
    case scorecardQType.multiple_choice:
      return (
        <QuestionController<string[]>
          required={isDraft ? false : question.is_required}
          name={`${formFieldPrefix}.${index}.mc_answer`}
        >
          {({ onChange, value, error }) =>
            question.multi_select ? (
              <>
                <MultiSelectMultipleChoice
                  dataTestId={`${testIdPrefix}-multiple-choice-${question.id}`}
                  isRequired={question.is_required}
                  items={(question.mc_options || []).map((option) => ({
                    id: option.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0),
                    isChecked: false,
                    title: option,
                  }))}
                  question={question.question_text}
                  onChange={onChange}
                  defaultValue={answers?.multipleChoice}
                  isReadOnly={isReadOnly}
                />
                {error && (
                  <ErrorText
                    dataTestId={`${testIdPrefix}-multiple-choice-${question.id}-error-text`}
                  />
                )}
              </>
            ) : (
              <>
                <SingleMultipleChoiceQuestion
                  dataTestId={`${testIdPrefix}-multiple-choice-${question.id}`}
                  defaultValue={value}
                  isRequired={question.is_required}
                  items={question.mc_options || []}
                  onChange={onChange}
                  question={question.question_text}
                  isReadOnly={isReadOnly}
                />
                {error && (
                  <ErrorText
                    dataTestId={`${testIdPrefix}-multiple-choice-${question.id}-error-text`}
                  />
                )}
              </>
            )
          }
        </QuestionController>
      );
    case scorecardQType.cumulative_score:
      return (
        <QuestionController
          required={isDraft ? false : question.is_required}
          name="cumulative_score"
        >
          {({ error }) => {
            const cumulativeScore = watch('cumulative_score');
            const maxCumulativeScore = watch('max_cumulative_score');

            return (
              <>
                <CumulativeScoreQuestion
                  dataTestId={`${testIdPrefix}-cumulative-score-${question.id}`}
                  maxScore={maxCumulativeScore}
                  score={cumulativeScore}
                  text={'Cumulative score:'}
                />
                {error && (
                  <ErrorText
                    dataTestId={`${testIdPrefix}-cumulative-score-${question.id}-error-text`}
                  />
                )}
              </>
            );
          }}
        </QuestionController>
      );
    case scorecardQType.direction_text:
      return (
        <Typography
          dangerouslySetInnerHTML={{
            __html: question.directions,
          }}
          data-testid={`${testIdPrefix}-direction-text-${question.id}`}
          variant="body1"
        />
      );
    case scorecardQType.final_recommendation:
      return (
        <QuestionController<FinalRecommendation>
          required={isDraft ? false : question.is_required}
          name="final_recommendation"
        >
          {({ onChange, value, error }) => (
            <>
              <FinalRecommendationQuestion
                dataTestId={`${testIdPrefix}-final-recommendation-${question.id}`}
                defaultValue={value}
                includeMaybeOption={question.include_maybe_option}
                isRequired={question.is_required}
                onToggle={onChange}
                questionText={question.question_text}
                isReadOnly={isReadOnly}
              />
              {error && (
                <ErrorText
                  dataTestId={`${testIdPrefix}-final-recommendation-${question.id}-error-text`}
                />
              )}
            </>
          )}
        </QuestionController>
      );
    case scorecardQType.rubric:
      return (
        <QuestionController<number>
          required={isDraft ? false : question.is_required}
          name={`${formFieldPrefix}.${index}.answer_rubric`}
        >
          {({ onChange, value, error }) => (
            <>
              <RubricQuestion
                currentlySelected={value}
                dataTestId={`${testIdPrefix}-rubric-${question.id}`}
                isRequired={question.is_required}
                onChange={(newValue) => {
                  onChange(newValue);
                  onCumulativeScoreUpdate?.();
                }}
                rows={question.rubric_columns}
                title={question.question_text}
                isReadOnly={isReadOnly}
              />
              {error && (
                <ErrorText dataTestId={`${testIdPrefix}-rubric-${question.id}-error-text`} />
              )}
            </>
          )}
        </QuestionController>
      );
    case scorecardQType.attachment:
      return (
        <QuestionController<File>
          required={isDraft ? false : question.is_required}
          name={`${formFieldPrefix}.${index}.attachment`}
        >
          {({ onChange, value, error }) => (
            <>
              <AttachmentQuestion
                questionText={question.question_text}
                dataTestId={`${testIdPrefix}-attachment-${question.id}`}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fileData = e.target.files?.[0];
                  if (fileData) {
                    onChange(fileData);
                  }
                }}
                defaultValue={answers?.attachment || value?.name}
                isRequired={question?.is_required}
                isReadOnly={isReadOnly}
              />
              {error && (
                <ErrorText dataTestId={`${testIdPrefix}-attachment-${question.id}-error-text`} />
              )}
            </>
          )}
        </QuestionController>
      );
    default:
      return <div />;
  }
};
